import * as React from 'react'

import { Slider as SliderPrimitive } from '@base-ui/react/slider'
import {
  AlignJustifyIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronsLeftRightIcon,
  XIcon,
} from 'lucide-react'
import { ChromePicker } from 'react-color'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerTitle,
  DrawerTrigger,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import {
  Popover,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { Slider } from '~/client/src/shared/components/shadcn-ui/slider'
import {
  Tooltip,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  ArrowheadPosition,
  HIGHLIGHTER_OPACITY,
  MARKUP_DEFAULT_COLOR,
  NO_FILL,
  splitHexAlpha,
} from './MarkupDrawController'
import type { IStylePreview } from './MarkupEditorStore'
import {
  MarkupPopoverContent as PopoverContent,
  MarkupTooltipContent as TooltipContent,
} from './MarkupPortalContainer'
import {
  ArrowheadType,
  ARROWHEAD_TYPES,
  FillStyle,
  FILL_STYLES,
  StrokeStyle,
} from './markupTools'

import Colors from '~/client/src/shared/theme.module.scss'

interface IRgba {
  r: number
  g: number
  b: number
  a?: number
}
interface IColorResult {
  hex: string
  rgb: IRgba
}

// react-color works best with an {r,g,b,a} object for alpha; our colors persist
// as 6- or 8-digit hex, so convert at the boundary.
function hexToRgba(hex: string): IRgba {
  const m = (hex ?? '').replace('#', '')
  if (m.length < 6) return { r: 0, g: 0, b: 0, a: 1 }
  return {
    r: parseInt(m.slice(0, 2), 16),
    g: parseInt(m.slice(2, 4), 16),
    b: parseInt(m.slice(4, 6), 16),
    a: m.length >= 8 ? parseInt(m.slice(6, 8), 16) / 255 : 1,
  }
}
function rgbaToHex({ r, g, b, a = 1 }: IRgba): string {
  const h = (n: number): string => Math.round(n).toString(16).padStart(2, '0')
  const base = `#${h(r)}${h(g)}${h(b)}`
  return a >= 1 ? base : base + h(a * 255)
}

export const STROKE_MIN = 1
// Context-specific weight ranges: brushes (pencil/highlighter/eraser) go wide,
// lines/arrows stay readable, shape borders are outlines and stay thin.
export const STROKE_MAX = 72
export const LINE_MAX = 24
export const BORDER_MAX = 12
export const SEGMENT = 'tw:flex tw:items-center tw:gap-1 tw:p-1'
export const DIVIDER = 'tw:border-r tw:border-border'

// Preset markup palette. Ten content hues + a neutral
// on top, ten variants below — two rows. How transparency is offered is
// contextual (PaletteMode), but the layout is always those two rows:
//  - 'opaque' (pen / arrow / line / text / shape border): row 2 = opaque pastels
//    (hues over white); custom "joker" picker is opaque (no alpha slider).
//  - 'wash' (highlighter): the same two rows previewed translucent at the fixed
//    tool opacity; the stored color stays a plain hue (the tool applies the
//    opacity); the joker is opaque too — opacity is the tool's, not the user's.
//  - 'fill' (shape fill): row 2 = the hues' transparent analogues (real 8-digit
//    alpha); the joker keeps its alpha slider.
// Literal hexes (a designed content palette, not theme chrome).
const PALETTE_TINT = 0x4d / 255 // ~30% — opaque pastels = hues at this over white
const PALETTE_ALPHA = '4D' // ~30% — the transparent-analogue alpha for fills
const PALETTE_HUES = [
  Colors.mapBlack,
  Colors.mapGrey,
  Colors.mapRed,
  Colors.mapOrange,
  Colors.mapYellow,
  Colors.mapGreen,
  Colors.mapTeal,
  Colors.mapBlue,
  Colors.mapPurple,
  Colors.mapPink,
]

// mixWithWhite stays as a defensive fallback but is no longer reached (every hue
// has a tint).
const MAP_TINTS: Record<string, string> = {
  [Colors.mapBlack]: Colors.mapBlackTint,
  [Colors.mapGrey]: Colors.mapGreyTint,
  [Colors.mapRed]: Colors.mapRedTint,
  [Colors.mapOrange]: Colors.mapOrangeTint,
  [Colors.mapYellow]: Colors.mapYellowTint,
  [Colors.mapGreen]: Colors.mapGreenTint,
  [Colors.mapTeal]: Colors.mapTealTint,
  [Colors.mapBlue]: Colors.mapBlueTint,
  [Colors.mapPurple]: Colors.mapPurpleTint,
  [Colors.mapPink]: Colors.mapPinkTint,
}

// Each hue's saturated "-deep" border token (FigJam parity: an opaque fill pairs
// with a deeper border). Black/grey/white have no deep — they fall back to base.
const MAP_DEEPS: Record<string, string> = {
  [Colors.mapRed]: Colors.mapRedDeep,
  [Colors.mapOrange]: Colors.mapOrangeDeep,
  [Colors.mapYellow]: Colors.mapYellowDeep,
  [Colors.mapGreen]: Colors.mapGreenDeep,
  [Colors.mapTeal]: Colors.mapTealDeep,
  [Colors.mapBlue]: Colors.mapBlueDeep,
  [Colors.mapPurple]: Colors.mapPurpleDeep,
  [Colors.mapPink]: Colors.mapPinkDeep,
}

// FigJam parity: the border tracks a more-saturated version of the fill — a
// translucent fill → the opaque base hue; an opaque fill → its "-deep" token.
export function deriveBorderColor(fillColor: string): string | null {
  if (!fillColor || fillColor === NO_FILL) return null
  const { color: base, opacity } = splitHexAlpha(fillColor)
  if (opacity < 1) return base
  return MAP_DEEPS[base.toLowerCase()] ?? base
}

export type PaletteMode = 'opaque' | 'wash' | 'fill'

export const DRAW_DEFAULT_STYLE = {
  color: MARKUP_DEFAULT_COLOR,
  strokeWidth: 3,
}
// Rendered translucent by the tool — the stored color stays opaque.
export const HIGHLIGHTER_DEFAULT_STYLE = {
  color: MARKUP_DEFAULT_COLOR,
  strokeWidth: 24,
}
export const SHAPE_DEFAULT_STYLE = {
  fillColor: NO_FILL,
  color: MARKUP_DEFAULT_COLOR,
  strokeWidth: 2,
}
export const LINE_DEFAULT_STYLE = {
  color: MARKUP_DEFAULT_COLOR,
  strokeWidth: 3,
}

interface IPaletteSwatch {
  value: string
  previewAlpha?: number
}

// Blend a hex toward white by (1 - frac) — the opaque equivalent of `hex` drawn
// at `frac` opacity over a white surface (the opaque-pastel second row).
function mixWithWhite(hex: string, frac: number): string {
  const { r, g, b } = hexToRgba(hex)
  const c = (n: number): string =>
    Math.round(n * frac + 255 * (1 - frac))
      .toString(16)
      .padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`
}

// The color grid (10 hues × 2 rows). Row 1 is the opaque hues; row 2 is opaque
// pastels (opaque/wash) or transparent analogues (fill). 'wash' previews every
// swatch translucent at the tool opacity. In non-alpha modes a hue's pastel is
// its map/<hue>-tint token; any hue without one falls back to a computed mix.
// The non-hue controls (custom, white, no-fill) live in a separate column.
function paletteSwatches(mode: PaletteMode): IPaletteSwatch[] {
  const previewAlpha = mode === 'wash' ? HIGHLIGHTER_OPACITY : undefined
  if (mode === 'fill') {
    return [
      ...PALETTE_HUES.map(value => ({ value })),
      ...PALETTE_HUES.map(value => ({ value: value + PALETTE_ALPHA })),
    ]
  }
  const row2 = PALETTE_HUES.map(
    hue => MAP_TINTS[hue] ?? mixWithWhite(hue, PALETTE_TINT),
  )
  return [...PALETTE_HUES, ...row2].map(value => ({ value, previewAlpha }))
}

const RAINBOW_GRADIENT =
  'conic-gradient(from 90deg, #f43f5e, #f59e0b, #facc15, #22c55e, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #f43f5e)'

// Transparency checkerboard (theme-grey squares over the popover surface) shown
// behind a translucent swatch so its alpha reads — the classic alpha indicator.
const CHECKERBOARD: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, var(--border) 25%, transparent 25%), ' +
    'linear-gradient(-45deg, var(--border) 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, var(--border) 75%), ' +
    'linear-gradient(-45deg, transparent 75%, var(--border) 75%)',
  backgroundSize: '12px 12px',
  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
}

// A round color chip for `color` (6- or 8-digit hex). If the color carries alpha,
// or a `previewAlpha` override is given (wash mode previews an opaque hue as
// translucent), it's drawn at that opacity over a transparency checkerboard so
// the see-through reads; otherwise it's a solid fill.
const SwatchDot: React.FC<{
  color: string
  previewAlpha?: number
  className?: string
  sizeClass?: string
}> = ({ color, previewAlpha, className = '', sizeClass = 'tw:size-6' }) => {
  if (color === NO_FILL) {
    return (
      <span
        className={`tw:relative ${sizeClass} tw:overflow-hidden tw:rounded-full tw:border tw:border-border tw:bg-background ${className}`}
      >
        <span className="tw:absolute tw:top-1/2 tw:left-1/2 tw:h-[1.5px] tw:w-8 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:-rotate-45 tw:bg-border" />
      </span>
    )
  }
  const { color: base, opacity } = splitHexAlpha(color)
  const alpha = previewAlpha ?? (opacity < 1 ? opacity : undefined)
  return alpha == null ? (
    <span
      className={`${sizeClass} tw:rounded-full tw:border tw:border-border ${className}`}
      style={{ backgroundColor: base }}
    />
  ) : (
    <span
      className={`tw:relative ${sizeClass} tw:overflow-hidden tw:rounded-full tw:border tw:border-border ${className}`}
      style={CHECKERBOARD}
    >
      <span
        className="tw:absolute tw:inset-0"
        style={{ backgroundColor: base, opacity: alpha }}
      />
    </span>
  )
}

// forwardRef + prop spread so base-ui can use it as a `render={<Swatch/>}` target
// (e.g. a Tooltip trigger).
const Swatch = React.forwardRef<
  HTMLButtonElement,
  {
    value: string
    isSelected: boolean
    onSelect: () => void
    previewAlpha?: number
  } & React.ComponentProps<typeof Button>
>(function Swatch(
  { value, isSelected, onSelect, previewAlpha, onClick, ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      type="button"
      variant="ghost"
      size="icon"
      aria-label={value}
      aria-pressed={isSelected}
      className="tw:size-9 tw:rounded-md"
      {...rest}
      // Compose any injected onClick (e.g. base-ui Tooltip trigger) with select,
      // and keep it last so the spread above can't clobber the select handler.
      onClick={e => {
        onClick?.(e)
        onSelect()
      }}
    >
      <SwatchDot
        color={value}
        previewAlpha={previewAlpha}
        className={
          isSelected
            ? 'tw:ring-2 tw:ring-primary tw:ring-offset-2 tw:ring-offset-popover'
            : ''
        }
      />
    </Button>
  )
})

/**
 * Preset-palette grid shared by the color control and the Shape fill/border menus.
 * `mode` tailors which swatches show + whether the custom picker exposes alpha.
 * `onPick` fires only after a PRESET swatch (e.g. to close a single-purpose popover);
 * the custom picker keeps the popover open.
 */
const ColorPalette: React.FC<{
  color: string
  onChange: (color: string) => void
  onPick?: () => void
  mode?: PaletteMode
}> = ({ color, onChange, onPick, mode = 'opaque' }) => {
  const [showCustom, setShowCustom] = React.useState(false)
  const normalized = (color ?? '').toLowerCase()

  if (showCustom) {
    return (
      // data-vaul-no-drag: the picker's saturation/hue/alpha drags must not be
      // hijacked by the bottom-sheet's drag-to-dismiss gesture.
      <div data-vaul-no-drag="">
        <ChromePicker
          disableAlpha={mode !== 'fill'}
          color={hexToRgba(color)}
          onChangeComplete={(c: IColorResult) => onChange(rgbaToHex(c.rgb))}
        />
      </div>
    )
  }
  const previewAlpha = mode === 'wash' ? HIGHLIGHTER_OPACITY : undefined
  // Mark the custom (joker) swatch selected when the current color matches no
  // preset — e.g. a derived "-deep" border has no swatch, so it reads as custom.
  const presets = new Set([
    ...paletteSwatches(mode).map(s => s.value.toLowerCase()),
    Colors.mapWhite.toLowerCase(),
    ...(mode === 'fill' ? [NO_FILL.toLowerCase()] : []),
  ])
  const isCustomSelected = !!normalized && !presets.has(normalized)
  const renderSwatch = ({
    value,
    previewAlpha: a,
  }: IPaletteSwatch): JSX.Element => (
    <Swatch
      key={value}
      value={value}
      previewAlpha={a}
      isSelected={value.toLowerCase() === normalized}
      onSelect={() => {
        onChange(value)
        onPick?.()
      }}
    />
  )
  return (
    <div className="tw:flex tw:gap-0.5">
      {mode === 'fill' && (
        <div className="tw:mr-0.5 tw:flex tw:items-center tw:border-r tw:border-border tw:pr-1.5">
          <Tooltip>
            <TooltipTrigger
              render={
                <Swatch
                  value={NO_FILL}
                  isSelected={NO_FILL.toLowerCase() === normalized}
                  onSelect={() => {
                    onChange(NO_FILL)
                    onPick?.()
                  }}
                />
              }
            />
            <TooltipContent>No fill</TooltipContent>
          </Tooltip>
        </div>
      )}
      <div className="tw:grid tw:grid-cols-10 tw:gap-0.5">
        {paletteSwatches(mode).map(renderSwatch)}
      </div>
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        {renderSwatch({ value: Colors.mapWhite, previewAlpha })}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={Localization.translator.customColor}
          onClick={() => setShowCustom(true)}
          className="tw:size-9 tw:rounded-md"
        >
          <span
            className={`tw:size-6 tw:rounded-full tw:border tw:border-border ${
              isCustomSelected
                ? 'tw:ring-2 tw:ring-primary tw:ring-offset-2 tw:ring-offset-popover'
                : ''
            }`}
            style={{ background: RAINBOW_GRADIENT }}
          />
        </Button>
      </div>
    </div>
  )
}

// Mobile surface for a picker: the desktop Popover's content in a bottom Drawer.
// The overlay is transparent + blur-free so the live change stays visible on the
// map behind the sheet. Edits preview live; closing/Cancel reverts; only Apply
// commits (see store.stylePreview, wired via begin/apply/cancel).
export const MarkupPickerSheet: React.FC<{
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  trigger: React.ReactNode
  children: React.ReactNode
  stylePreview?: IStylePreview
}> = ({ open, onOpenChange, title, trigger, children, stylePreview }) => {
  const appliedRef = React.useRef(false)
  const handleOpenChange = (next: boolean): void => {
    if (next) {
      appliedRef.current = false
      stylePreview?.begin()
    } else if (!appliedRef.current) {
      // Dismissed via X / outside-tap / swipe — revert the live preview.
      stylePreview?.cancel()
    }
    onOpenChange(next)
  }
  const apply = (): void => {
    appliedRef.current = true
    stylePreview?.apply()
    onOpenChange(false)
  }
  return (
    // noBodyStyles: skip vaul's `position: fixed` body-lock — it collapses the
    // full-height layout under the map and makes it jump when the sheet opens
    // (same reason MapSettingsDrawer sets it).
    <Drawer open={open} onOpenChange={handleOpenChange} noBodyStyles>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent
        overlayStyle={{
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
        }}
      >
        <div className="tw:flex tw:items-center tw:gap-1 tw:px-1 tw:pt-1">
          <DrawerClose asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={Localization.translator.close}
            >
              <XIcon aria-hidden="true" />
            </Button>
          </DrawerClose>
          <DrawerTitle className="tw:flex-1 tw:text-center">
            {title}
          </DrawerTitle>
          <span aria-hidden className="tw:size-9 tw:shrink-0" />
        </div>
        {/* Satisfies Radix's required description without adding visible chrome. */}
        <DrawerDescription className="tw:sr-only">{title}</DrawerDescription>
        <div className="tw:px-3">{children}</div>
        <DrawerFooter className="tw:flex-row tw:pt-3">
          <DrawerClose asChild>
            <Button type="button" variant="outline" className="tw:flex-1">
              Cancel
            </Button>
          </DrawerClose>
          <Button type="button" onClick={apply} className="tw:flex-1">
            Apply
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

/**
 * Color control shared by the line/text toolbars (single stroke/text color).
 * On mobile the palette opens in a bottom sheet instead of a popover.
 */
export const ColorSegment: React.FC<{
  color: string
  onChange: (color: string) => void
  mode?: PaletteMode
  /** Render the trigger as a letter over a colored bar (Google Docs text-color
   * style) instead of the swatch dot. */
  glyph?: string
  label?: string
  isMobileEnv?: boolean
  stylePreview?: IStylePreview
}> = ({
  color,
  onChange,
  mode = 'opaque',
  glyph,
  label = 'Color',
  isMobileEnv,
  stylePreview,
}) => {
  const [open, setOpen] = React.useState(false)

  const triggerInner = (
    <>
      {glyph ? (
        <span className="tw:flex tw:flex-col tw:items-center tw:gap-[2px] tw:leading-none">
          <span className="tw:text-[15px] tw:font-semibold tw:text-foreground">
            {glyph}
          </span>
          <span
            aria-hidden
            className="tw:h-[3px] tw:w-[15px] tw:rounded-full"
            style={{ backgroundColor: color }}
          />
        </span>
      ) : (
        <SwatchDot
          color={color}
          previewAlpha={mode === 'wash' ? HIGHLIGHTER_OPACITY : undefined}
          sizeClass="tw:size-5"
        />
      )}
      <ChevronDownIcon className="tw:text-muted-foreground" aria-hidden />
    </>
  )
  const palette = (
    <ColorPalette
      color={color}
      onChange={onChange}
      // Mobile: keep the sheet open after a pick so the user can preview more and
      // Apply. Desktop popover closes on pick (unchanged).
      onPick={isMobileEnv ? undefined : () => setOpen(false)}
      mode={mode}
    />
  )

  return (
    <div className={`${SEGMENT} ${DIVIDER}`}>
      {isMobileEnv ? (
        <MarkupPickerSheet
          open={open}
          onOpenChange={setOpen}
          title={label}
          stylePreview={stylePreview}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={label}
              className="tw:gap-1"
            >
              {triggerInner}
            </Button>
          }
        >
          {palette}
        </MarkupPickerSheet>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={label}
                className="tw:gap-1"
              />
            }
          >
            {triggerInner}
          </PopoverTrigger>
          <PopoverContent align="start" className="tw:w-auto tw:p-2">
            {palette}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

// 'cloud' is shown but disabled — scalloped-border rendering isn't implemented yet.
const BORDER_PATTERNS: {
  value: StrokeStyle
  label: string
  disabled?: boolean
}[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'dashed', label: 'Dashed' },
  // Cloud (scalloped) stroke hidden until implemented — STX1-627 (Markup Editor V2):
  // https://linear.app/struxhub/issue/STX1-627/cloud-scalloped-stroke-style
  // { value: 'cloud', label: 'Cloud', disabled: true },
]

const CLOUD_PATTERN_PATH =
  'M7.72461 0C11.9903 0.000263958 15.4482 3.58188 15.4482 8H13.5176C13.5176 4.68645 10.9238 2.00026 7.72461 2C4.52517 2 1.93066 4.68629 1.93066 8H0C0 3.58172 3.45869 0 7.72461 0Z' +
  'M21.2419 0C25.5076 0.000263958 28.9655 3.58188 28.9655 8H27.0349C27.0349 4.68645 24.4411 2.00026 21.2419 2C18.0425 2 15.448 4.68629 15.448 8H13.5173C13.5173 3.58172 16.976 0 21.2419 0Z' +
  'M34.759 0C39.0247 0.000263958 42.4827 3.58188 42.4827 8H40.552C40.552 4.68645 37.9583 2.00026 34.759 2C31.5596 2 28.9651 4.68629 28.9651 8H27.0344C27.0344 3.58172 30.4931 0 34.759 0Z' +
  'M48.2761 0C52.5419 0.000263958 55.9998 3.58188 55.9998 8H54.0691C54.0691 4.68645 51.4754 2.00026 48.2761 2C45.0767 2 42.4822 4.68629 42.4822 8H40.5515C40.5515 3.58172 44.0102 0 48.2761 0Z'

function BorderPatternPreview({ style }: { style: StrokeStyle }): JSX.Element {
  if (style === 'cloud') {
    return (
      <svg
        width="56"
        height="8"
        viewBox="0 0 56 8"
        fill="currentColor"
        aria-hidden="true"
        className="tw:size-auto"
      >
        <path d={CLOUD_PATTERN_PATH} />
      </svg>
    )
  }
  return (
    <svg
      width="56"
      height="2"
      viewBox="0 0 56 2"
      fill="none"
      aria-hidden="true"
      className="tw:size-auto"
    >
      <line
        x1="1"
        y1="1"
        x2="55"
        y2="1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray={style === 'dashed' ? '6 6' : undefined}
      />
    </svg>
  )
}

// Decorative wedge track behind the weight slider — conveys thin → thick.
function WedgeTrack(): JSX.Element {
  return (
    <svg
      preserveAspectRatio="none"
      viewBox="0 0 104 8"
      fill="currentColor"
      aria-hidden="true"
      className="tw:absolute tw:inset-x-0 tw:top-1/2 tw:h-2 tw:w-full tw:-translate-y-1/2 tw:text-border"
    >
      <path d="M0 6L104 0V8H0V6Z" />
    </svg>
  )
}

const WedgeSlider: React.FC<{
  width: number
  onChange: (width: number) => void
  max: number
}> = ({ width, onChange, max }) => (
  <SliderPrimitive.Root
    className="tw:relative tw:flex tw:h-3 tw:w-full tw:touch-none tw:items-center tw:select-none"
    thumbAlignment="edge"
    min={STROKE_MIN}
    max={max}
    value={width}
    onValueChange={v => onChange(v)}
    aria-label={Localization.translator.strokeWidthSlider}
  >
    <WedgeTrack />
    <SliderPrimitive.Control className="tw:relative tw:flex tw:h-full tw:w-full tw:items-center">
      <SliderPrimitive.Track className="tw:h-2 tw:w-full tw:bg-transparent">
        <SliderPrimitive.Thumb className="tw:box-border tw:size-4 tw:cursor-pointer tw:rounded-full tw:border-2 tw:border-popover tw:bg-primary tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary" />
      </SliderPrimitive.Track>
    </SliderPrimitive.Control>
  </SliderPrimitive.Root>
)

// Shared weight-entry rule: a free-form draft (clearable, focus-selects) committing
// on blur/Enter, clamped only then so any value can be typed; out-of-range
// keystrokes are rejected so only valid values stick.
function useWeightDraft(
  width: number,
  onChange: (width: number) => void,
  max: number,
): { draft: string; inputProps: React.InputHTMLAttributes<HTMLInputElement> } {
  const clamp = (n: number): number => Math.min(max, Math.max(STROKE_MIN, n))
  const [draft, setDraft] = React.useState(String(width))
  React.useEffect(() => setDraft(String(width)), [width])

  const commit = (): void => {
    const n = Number(draft)
    if (draft.trim() === '' || Number.isNaN(n)) {
      setDraft(String(width))
      return
    }
    const next = clamp(n)
    setDraft(String(next))
    if (next !== width) onChange(next)
  }

  return {
    draft,
    inputProps: {
      type: 'text',
      inputMode: 'numeric',
      value: draft,
      onFocus: e => {
        const el = e.currentTarget
        requestAnimationFrame(() => el.select())
      },
      onChange: e => {
        const v = e.target.value
        if (v === '') {
          setDraft('')
          return
        }
        if (!/^\d{1,3}$/.test(v) || Number(v) > max) return
        setDraft(v)
        const n = Number(v)
        if (n >= STROKE_MIN) onChange(n)
      },
      onBlur: commit,
      onKeyDown: e => {
        if (e.key === 'Enter') {
          commit()
          e.currentTarget.blur()
        }
      },
    },
  }
}

// Shared by the Border popover and the Draw / freehand toolbars.
export const WeightControl: React.FC<{
  width: number
  onChange: (width: number) => void
  max?: number
}> = ({ width, onChange, max = STROKE_MAX }) => {
  const { draft, inputProps } = useWeightDraft(width, onChange, max)

  return (
    <div className="tw:flex tw:w-28 tw:flex-col tw:items-start tw:px-1">
      <div className="tw:flex tw:items-baseline tw:gap-0.5 tw:text-sm tw:font-medium tw:tracking-[-0.084px] tw:text-foreground">
        <input
          {...inputProps}
          aria-label={Localization.translator.strokeWidth}
          style={{ width: `calc(${Math.max(1, draft.length)}ch + 2px)` }}
          className="tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:tabular-nums tw:outline-none"
        />
        <span>px</span>
      </div>
      <WedgeSlider width={width} max={max} onChange={onChange} />
    </div>
  )
}

// Mobile thickness slider. The native range drives drag/value/a11y (invisible on
// top); the visible thumb is an overlay positioned at the value fraction. Shared by
// every mobile weight sheet (border, draw thickness, freehand).
const ThicknessSlider: React.FC<{
  width: number
  onChange: (width: number) => void
  max?: number
}> = ({ width, onChange, max = STROKE_MAX }) => {
  const { draft, inputProps } = useWeightDraft(width, onChange, max)
  return (
    // data-vaul-no-drag: keep vaul from reading a drag here as a dismiss swipe.
    <div
      className="tw:flex tw:w-full tw:items-center tw:gap-3"
      data-vaul-no-drag=""
    >
      <label className="tw:flex tw:h-10 tw:w-[72px] tw:shrink-0 tw:items-center tw:justify-center tw:gap-0.5 tw:rounded tw:border tw:border-border tw:text-sm tw:font-medium tw:tabular-nums tw:text-foreground tw:focus-within:ring-2 tw:focus-within:ring-primary">
        <input
          {...inputProps}
          enterKeyHint="done"
          aria-label={Localization.translator.borderWidth}
          style={{ width: `${Math.max(1, draft.length)}ch` }}
          className="tw:border-0 tw:bg-transparent tw:p-0 tw:text-right tw:tabular-nums tw:outline-none"
        />
        <span>px</span>
      </label>
      <Slider
        className="tw:h-6 tw:flex-1"
        size="lg"
        min={STROKE_MIN}
        max={max}
        value={width}
        onValueChange={v => onChange(v)}
        aria-label={Localization.translator.borderWidthSlider}
      >
        <ChevronsLeftRightIcon
          className="tw:text-primary-foreground"
          aria-hidden
        />
      </Slider>
    </div>
  )
}

/** Standalone weight control: desktop inlines WeightControl; mobile opens a
 * "Thickness" sheet (consistent with the other mobile picker sheets). */
export const WeightSegment: React.FC<{
  width: number
  onChange: (width: number) => void
  max?: number
  isMobileEnv?: boolean
  stylePreview?: IStylePreview
}> = ({ width, onChange, max = STROKE_MAX, isMobileEnv, stylePreview }) => {
  const [open, setOpen] = React.useState(false)

  if (!isMobileEnv) {
    return (
      <div className={`tw:flex tw:items-center ${DIVIDER}`}>
        <WeightControl width={width} onChange={onChange} max={max} />
      </div>
    )
  }

  return (
    <div className={`${SEGMENT} ${DIVIDER}`}>
      <MarkupPickerSheet
        open={open}
        onOpenChange={setOpen}
        title={Localization.translator.thickness}
        stylePreview={stylePreview}
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={Localization.translator.thickness}
            className="tw:gap-1 tw:text-foreground"
          >
            <AlignJustifyIcon className="tw:size-[18px]" aria-hidden />
            <span className="tw:text-sm tw:font-medium tw:tabular-nums">
              {width} px
            </span>
            <ChevronDownIcon className="tw:text-muted-foreground" aria-hidden />
          </Button>
        }
      >
        <div className="tw:py-2">
          <ThicknessSlider width={width} onChange={onChange} max={max} />
        </div>
      </MarkupPickerSheet>
    </div>
  )
}

/** Border control (context toolbar): editable weight + style chips. When
 * `color`/`onColorChange` are passed (Shape border), a stroke-color palette is
 * appended below. Cloud is shown disabled until scalloped rendering lands. */
export const BorderSegment: React.FC<{
  width: number
  strokeStyle: StrokeStyle
  onWidthChange: (width: number) => void
  onStrokeStyleChange: (style: StrokeStyle) => void
  color?: string
  onColorChange?: (color: string) => void
  max?: number
  isMobileEnv?: boolean
  stylePreview?: IStylePreview
}> = ({
  width,
  strokeStyle,
  onWidthChange,
  onStrokeStyleChange,
  color,
  onColorChange,
  max,
  isMobileEnv,
  stylePreview,
}) => {
  const [open, setOpen] = React.useState(false)

  const triggerInner = (
    <>
      <AlignJustifyIcon className="tw:size-[18px]" aria-hidden />
      <ChevronDownIcon className="tw:text-muted-foreground" aria-hidden />
    </>
  )
  // Multi-control menu (weight + style chips): never auto-closes on select, on
  // either platform — the user tweaks several controls before committing.
  const chips = BORDER_PATTERNS.map(({ value, label, disabled }) => (
    <Button
      key={value}
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      aria-pressed={strokeStyle === value}
      onClick={() => onStrokeStyleChange(value)}
      className={
        strokeStyle === value
          ? 'tw:h-8 tw:flex-col tw:items-start tw:gap-0.5 tw:border tw:border-primary tw:bg-primary/8 tw:px-2 tw:text-foreground tw:hover:bg-primary/8'
          : 'tw:h-8 tw:flex-col tw:items-start tw:gap-0.5 tw:px-2 tw:text-foreground'
      }
    >
      <span className="tw:text-xs tw:font-medium tw:leading-4">{label}</span>
      <BorderPatternPreview style={value} />
    </Button>
  ))
  const palette =
    color != null && onColorChange ? (
      <ColorPalette color={color} onChange={onColorChange} />
    ) : null

  const body = (
    <>
      <div className="tw:flex tw:flex-row tw:items-start tw:gap-2 tw:p-2">
        <WeightControl width={width} onChange={onWidthChange} max={max} />
        {chips}
      </div>
      {palette && (
        <div className="tw:border-t tw:border-border tw:p-2">{palette}</div>
      )}
    </>
  )

  const mobileBody = (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2">
      <ThicknessSlider width={width} onChange={onWidthChange} max={max} />
      <div className="tw:flex tw:gap-1">{chips}</div>
      {palette}
    </div>
  )

  // Shapes get the border color inline ("Border Properties"); lines style the
  // stroke elsewhere, so their sheet is just thickness + dash ("Line Style").
  const title = color != null ? 'Border Properties' : 'Line Style'

  return (
    <div className={`${SEGMENT} ${DIVIDER}`}>
      {isMobileEnv ? (
        <MarkupPickerSheet
          open={open}
          onOpenChange={setOpen}
          title={title}
          stylePreview={stylePreview}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={Localization.translator.border}
              className="tw:gap-1 tw:text-foreground"
            >
              {triggerInner}
            </Button>
          }
        >
          {mobileBody}
        </MarkupPickerSheet>
      ) : (
        <Popover>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={Localization.translator.border}
                className="tw:gap-1 tw:text-foreground"
              />
            }
          >
            {triggerInner}
          </PopoverTrigger>
          <PopoverContent align="start" className="tw:w-auto tw:gap-0 tw:p-0">
            {body}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

// `flip` mirrors the cap for the start endpoint (cap on the left).
function CapPreview({
  type,
  flip,
}: {
  type: ArrowheadType
  flip?: boolean
}): JSX.Element {
  return (
    <svg
      width="28"
      height="12"
      viewBox="0 0 28 12"
      aria-hidden="true"
      className="tw:size-auto"
      style={flip ? { transform: 'scaleX(-1)' } : undefined}
    >
      {type === 'circle' ? (
        <>
          <line
            x1="2"
            y1="6"
            x2="20"
            y2="6"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <circle cx="22" cy="6" r="4" fill="currentColor" />
        </>
      ) : type === 'arrow' ? (
        <>
          <line
            x1="2"
            y1="6"
            x2="25"
            y2="6"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <polyline
            points="18,1 25,6 18,11"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <line
          x1="2"
          y1="6"
          x2="26"
          y2="6"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      )}
    </svg>
  )
}

/** A single endpoint-cap control (arrow/line context toolbar); Start and End get
 * separate instances. The trigger previews the current cap. */
export const ArrowheadSegment: React.FC<{
  position: ArrowheadPosition
  value: ArrowheadType
  onChange: (position: ArrowheadPosition, type: ArrowheadType) => void
  isMobileEnv?: boolean
  stylePreview?: IStylePreview
}> = ({ position, value, onChange, isMobileEnv, stylePreview }) => {
  const [open, setOpen] = React.useState(false)
  const flip = position === 'start'
  const name = position === 'start' ? 'Start point' : 'End point'
  const title = position === 'start' ? 'Start Point' : 'End Point'

  const triggerInner = (
    <>
      <CapPreview type={value} flip={flip} />
      <ChevronDownIcon className="tw:text-muted-foreground" aria-hidden />
    </>
  )
  // Single-choice list: desktop closes on pick. Mobile previews live but stays
  // open so the user can compare caps and Apply.
  const body = ARROWHEAD_TYPES.map(({ value: option, label }) => (
    <Button
      key={option}
      type="button"
      variant="ghost"
      size="sm"
      aria-label={`${name} ${label}`}
      aria-pressed={value === option}
      onClick={() => {
        onChange(position, option)
        if (!isMobileEnv) setOpen(false)
      }}
      className={
        value === option
          ? 'tw:w-full tw:justify-between tw:gap-4 tw:bg-primary/8 tw:text-primary tw:hover:bg-primary/8'
          : 'tw:w-full tw:justify-between tw:gap-4 tw:text-foreground'
      }
    >
      <CapPreview type={option} flip={flip} />
      {value === option && (
        <CheckIcon className="tw:size-4" aria-hidden="true" />
      )}
    </Button>
  ))

  return (
    <div className={`${SEGMENT} ${DIVIDER}`}>
      {isMobileEnv ? (
        <MarkupPickerSheet
          open={open}
          onOpenChange={setOpen}
          title={title}
          stylePreview={stylePreview}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={name}
              className="tw:gap-1 tw:text-foreground"
            >
              {triggerInner}
            </Button>
          }
        >
          <div className="tw:flex tw:flex-col tw:gap-1">{body}</div>
        </MarkupPickerSheet>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={name}
                className="tw:gap-1 tw:text-foreground"
              />
            }
          >
            {triggerInner}
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="tw:flex tw:w-auto tw:flex-col tw:gap-1 tw:p-1"
          >
            {body}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

// Patterned fills are disabled for now, so these previews read as samples.
function FillPatternPreview({ pattern }: { pattern: FillStyle }): JSX.Element {
  const props = {
    width: 56,
    height: 9,
    viewBox: '0 0 56 9',
    'aria-hidden': true as const,
    className: 'tw:size-auto',
  }
  if (pattern === 'solid') {
    return (
      <svg {...props} fill="currentColor">
        <rect width="56" height="9" />
      </svg>
    )
  }
  if (pattern === 'dots') {
    return (
      <svg {...props} fill="currentColor">
        {[2.5, 6.5].flatMap(cy =>
          Array.from({ length: 12 }, (_, i) => (
            <circle key={`${cy}-${i}`} cx={3 + i * 5} cy={cy} r="1" />
          )),
        )}
      </svg>
    )
  }
  if (pattern === 'diagonal') {
    return (
      <svg {...props} fill="none" stroke="currentColor" strokeWidth="1">
        {Array.from({ length: 14 }, (_, i) => {
          const x = -9 + i * 5
          return <line key={i} x1={x} y1="9" x2={x + 9} y2="0" />
        })}
      </svg>
    )
  }
  if (pattern === 'grid') {
    return (
      <svg {...props} fill="none" stroke="currentColor" strokeWidth="0.75">
        <rect x="0.5" y="0.5" width="55" height="8" />
        {Array.from({ length: 7 }, (_, i) => (
          <line key={i} x1={(i + 1) * 7} y1="0" x2={(i + 1) * 7} y2="9" />
        ))}
        <line x1="0" y1="4.5" x2="56" y2="4.5" />
      </svg>
    )
  }
  // bricks: two offset rows of outlined rectangles (SVG root clips overflow)
  return (
    <svg {...props} fill="none" stroke="currentColor" strokeWidth="0.75">
      {Array.from({ length: 4 }, (_, i) => (
        <rect key={`t${i}`} x={i * 14 + 0.5} y="0.5" width="13" height="3.5" />
      ))}
      {Array.from({ length: 5 }, (_, i) => (
        <rect key={`b${i}`} x={i * 14 - 6.5} y="4.5" width="13" height="3.5" />
      ))}
    </svg>
  )
}

/** Shape color control: a fill-pattern row (Solid selectable, rest disabled) over
 * the fill-color palette. */
export const FillSegment: React.FC<{
  color: string
  fillStyle: FillStyle
  onColorChange: (color: string) => void
  onFillStyleChange: (style: FillStyle) => void
  isMobileEnv?: boolean
  stylePreview?: IStylePreview
}> = ({
  color,
  fillStyle,
  onColorChange,
  onFillStyleChange,
  isMobileEnv,
  stylePreview,
}) => {
  const [open, setOpen] = React.useState(false)

  const triggerInner = (
    <>
      <SwatchDot color={color} sizeClass="tw:size-5" />
      <ChevronDownIcon className="tw:text-muted-foreground" aria-hidden />
    </>
  )
  // Multi-control menu (pattern chips + fill palette): never auto-closes on
  // select, on either platform.
  const body = (
    <>
      <div className="tw:flex tw:flex-row tw:items-stretch tw:gap-2 tw:border-b tw:border-border tw:p-2">
        {FILL_STYLES.map(({ value, label, disabled }) => (
          <Button
            key={value}
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            aria-pressed={fillStyle === value}
            onClick={() => onFillStyleChange(value)}
            className={
              fillStyle === value
                ? 'tw:h-8 tw:flex-col tw:gap-0.5 tw:border tw:border-primary tw:bg-primary/8 tw:px-2 tw:text-foreground tw:hover:bg-primary/8'
                : 'tw:h-8 tw:flex-col tw:gap-0.5 tw:px-2 tw:text-foreground'
            }
          >
            <span className="tw:text-xs tw:font-medium tw:leading-4">
              {label}
            </span>
            <FillPatternPreview pattern={value} />
          </Button>
        ))}
      </div>
      <div className="tw:p-2">
        <ColorPalette color={color} onChange={onColorChange} mode="fill" />
      </div>
    </>
  )

  return (
    <div className={`${SEGMENT} ${DIVIDER}`}>
      {isMobileEnv ? (
        <MarkupPickerSheet
          open={open}
          onOpenChange={setOpen}
          title={Localization.translator.fill}
          stylePreview={stylePreview}
          trigger={
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={Localization.translator.fill}
              className="tw:gap-1"
            >
              {triggerInner}
            </Button>
          }
        >
          {body}
        </MarkupPickerSheet>
      ) : (
        <Popover>
          <PopoverTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={Localization.translator.fill}
                className="tw:gap-1"
              />
            }
          >
            {triggerInner}
          </PopoverTrigger>
          <PopoverContent align="start" className="tw:w-auto tw:gap-0 tw:p-0">
            {body}
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
